<template>
  <div class="accounting-page">
    <div class="page-header">
      <h2 class="page-title">足迹核算</h2>
    </div>

    <filter-bar>
      <div class="filter-item">
        <span class="filter-label">核算年度</span>
        <el-date-picker
          v-model="year"
          type="year"
          size="small"
          placeholder="全部"
          clearable
          value-format="yyyy"
          style="width: 120px"
        />
      </div>
      <div class="filter-item">
        <span class="filter-label">核算标准</span>
        <el-select v-model="standard" size="small" clearable placeholder="全部" style="width: 220px">
          <el-option v-for="item in standards" :key="item" :label="item" :value="item" />
        </el-select>
      </div>
      <div class="filter-item">
        <span class="filter-label">任务状态</span>
        <el-select v-model="status" size="small" clearable placeholder="全部" style="width: 120px">
          <el-option
            v-for="item in statusOptions"
            :key="item.value"
            :label="item.label"
            :value="item.value"
          />
        </el-select>
      </div>
      <el-button type="primary" size="small" @click="loadData">查询</el-button>
    </filter-bar>

    <div class="stat-cards-row">
      <stat-card
        title="核算任务数"
        :today="summary.total.today"
        :annual="summary.total.annual"
        :unit="summary.total.unit"
        :annual-unit="summary.total.unit"
        icon="el-icon-s-check"
        color="#409EFF"
      />
      <stat-card
        title="进行中"
        :today="summary.running.today"
        :annual="summary.running.annual"
        :unit="summary.running.unit"
        :annual-unit="summary.running.unit"
        icon="el-icon-loading"
        color="#E6A23C"
      />
      <stat-card
        title="已完成"
        :today="summary.completed.today"
        :annual="summary.completed.annual"
        :unit="summary.completed.unit"
        :annual-unit="summary.completed.unit"
        icon="el-icon-circle-check"
        color="#67C23A"
      />
      <stat-card
        title="待审核"
        :today="summary.review.today"
        :annual="summary.review.annual"
        :unit="summary.review.unit"
        :annual-unit="summary.review.unit"
        icon="el-icon-time"
        color="#1a3a5c"
      />
    </div>

    <div class="chart-section">
      <chart-card title="生命周期阶段贡献" class="stage-chart">
        <base-chart :option="stageChartOption" />
      </chart-card>
      <chart-card title="月度核算完成趋势" class="trend-chart">
        <base-chart :option="trendChartOption" />
      </chart-card>
    </div>

    <div class="table-section">
      <div class="table-toolbar">
        <el-button type="primary" size="small" icon="el-icon-plus" @click="openCreateDialog">
          发起核算
        </el-button>
      </div>
      <el-table :data="tasks" border stripe size="small">
        <el-table-column prop="taskNo" label="任务编号" width="130" />
        <el-table-column prop="productName" label="产品名称" min-width="160" show-overflow-tooltip />
        <el-table-column prop="modelName" label="关联模型" min-width="160" show-overflow-tooltip />
        <el-table-column prop="functionalUnit" label="功能单位" width="100" />
        <el-table-column prop="footprint" label="碳足迹 (kgCO2e)" width="140" align="right">
          <template slot-scope="{ row }">
            {{ row.footprint ? formatNumber(row.footprint, 2) : '-' }}
          </template>
        </el-table-column>
        <el-table-column prop="standard" label="核算标准" width="200" show-overflow-tooltip />
        <el-table-column prop="statusLabel" label="状态" width="90" align="center">
          <template slot-scope="{ row }">
            <el-tag :type="statusTagType(row.status)" size="mini">{{ row.statusLabel }}</el-tag>
          </template>
        </el-table-column>
        <el-table-column prop="completedAt" label="完成时间" width="120" />
        <el-table-column label="操作" width="220" align="center" fixed="right">
          <template slot-scope="{ row }">
            <el-button type="text" size="small" @click="openDetail(row)">查看结果</el-button>
            <el-button
              v-if="row.status === 'running'"
              type="text"
              size="small"
              @click="handleSubmit(row)"
            >提交审核</el-button>
            <el-button
              v-if="row.status === 'completed'"
              type="text"
              size="small"
              @click="goEvaluation(row)"
            >发起评价</el-button>
          </template>
        </el-table-column>
      </el-table>
    </div>

    <el-dialog
      title="发起核算"
      :visible.sync="createDialogVisible"
      width="520px"
      @close="resetCreateForm"
    >
      <el-form ref="createForm" :model="createForm" :rules="createRules" label-width="100px" size="small">
        <el-form-item label="关联模型" prop="modelId">
          <el-select v-model="createForm.modelId" placeholder="请选择已发布模型" style="width: 100%">
            <el-option
              v-for="item in publishedModels"
              :key="item.id"
              :label="item.name"
              :value="item.id"
            />
          </el-select>
        </el-form-item>
        <el-form-item label="功能单位" prop="functionalUnit">
          <el-input v-model="createForm.functionalUnit" placeholder="如 1件" />
        </el-form-item>
        <el-form-item label="核算年度" prop="year">
          <el-date-picker
            v-model="createForm.year"
            type="year"
            size="small"
            placeholder="选择年份"
            value-format="yyyy"
            style="width: 100%"
          />
        </el-form-item>
        <el-form-item label="系统边界">
          <el-input
            v-model="createForm.boundary"
            type="textarea"
            :rows="2"
            placeholder="如 从摇篮到大门"
          />
        </el-form-item>
      </el-form>
      <div slot="footer">
        <el-button size="small" @click="createDialogVisible = false">取消</el-button>
        <el-button type="primary" size="small" @click="handleCreate">确认发起</el-button>
      </div>
    </el-dialog>

    <el-drawer
      :title="currentTask ? currentTask.productName + ' 核算结果' : '核算结果'"
      :visible.sync="drawerVisible"
      size="520px"
      direction="rtl"
    >
      <div v-if="currentTask" class="drawer-content">
        <el-descriptions :column="1" border size="small" class="detail-block">
          <el-descriptions-item label="任务编号">{{ currentTask.taskNo }}</el-descriptions-item>
          <el-descriptions-item label="关联模型">{{ currentTask.modelName }}</el-descriptions-item>
          <el-descriptions-item label="功能单位">{{ currentTask.functionalUnit }}</el-descriptions-item>
          <el-descriptions-item label="核算标准">{{ currentTask.standard }}</el-descriptions-item>
          <el-descriptions-item label="碳足迹">
            {{ currentTask.footprint ? formatNumber(currentTask.footprint, 2) + ' kgCO2e' : '-' }}
          </el-descriptions-item>
          <el-descriptions-item label="状态">
            <el-tag :type="statusTagType(currentTask.status)" size="mini">
              {{ currentTask.statusLabel }}
            </el-tag>
          </el-descriptions-item>
        </el-descriptions>

        <div v-if="currentTask.stageBreakdown && currentTask.stageBreakdown.length" class="detail-block">
          <div class="block-title">生命周期阶段分项</div>
          <el-table :data="currentTask.stageBreakdown" border stripe size="small">
            <el-table-column prop="stage" label="阶段" width="120" />
            <el-table-column prop="emission" label="排放量 (kgCO2e)" align="right">
              <template slot-scope="{ row }">{{ formatNumber(row.emission, 2) }}</template>
            </el-table-column>
            <el-table-column prop="ratio" label="占比 (%)" width="100" align="right">
              <template slot-scope="{ row }">{{ formatNumber(row.ratio, 1) }}</template>
            </el-table-column>
          </el-table>
        </div>
      </div>
    </el-drawer>
  </div>
</template>

<script>
import FilterBar from '@/components/common/FilterBar.vue'
import StatCard from '@/components/common/StatCard.vue'
import ChartCard from '@/components/common/ChartCard.vue'
import BaseChart from '@/components/charts/BaseChart.vue'
import { getAccountingData } from '@/api/modules/carbonFootprint'
import { formatNumber } from '@/utils/format'

export default {
  name: 'CarbonFootprintAccounting',
  components: { FilterBar, StatCard, ChartCard, BaseChart },
  data() {
    return {
      year: '',
      standard: '',
      status: '',
      standards: [],
      statusOptions: [],
      summary: {
        total: { today: 0, annual: 0, unit: '项' },
        running: { today: 0, annual: 0, unit: '项' },
        completed: { today: 0, annual: 0, unit: '项' },
        review: { today: 0, annual: 0, unit: '项' }
      },
      tasks: [],
      allTasks: [],
      publishedModels: [],
      stageChartOption: {},
      trendChartOption: {},
      createDialogVisible: false,
      createForm: {
        modelId: '',
        functionalUnit: '',
        year: '2025',
        boundary: '从摇篮到大门'
      },
      createRules: {
        modelId: [{ required: true, message: '请选择关联模型', trigger: 'change' }],
        functionalUnit: [{ required: true, message: '请输入功能单位', trigger: 'blur' }],
        year: [{ required: true, message: '请选择核算年度', trigger: 'change' }]
      },
      drawerVisible: false,
      currentTask: null,
      nextTaskNo: 6
    }
  },
  created() {
    this.loadData()
  },
  methods: {
    formatNumber,
    async loadData() {
      const res = await getAccountingData({
        year: this.year,
        standard: this.standard,
        status: this.status
      })
      const data = res.data
      this.standards = data.standards
      this.statusOptions = data.statusOptions
      this.publishedModels = data.publishedModels
      if (!this.allTasks.length) {
        this.allTasks = data.tasks.map(item => ({
          ...item,
          stageBreakdown: item.stageBreakdown ? [...item.stageBreakdown] : []
        }))
      }
      this.applyFilters()
      this.buildStageChart(data.stageContribution)
      this.buildTrendChart(data.monthlyTrend)
    },
    applyFilters() {
      let list = [...this.allTasks]
      if (this.year) {
        list = list.filter(item => String(item.year) === String(this.year))
      }
      if (this.standard) {
        list = list.filter(item => item.standard === this.standard)
      }
      if (this.status) {
        list = list.filter(item => item.status === this.status)
      }
      this.tasks = list
      this.updateSummary(list)
    },
    updateSummary(list) {
      this.summary = {
        total: { today: list.length, annual: list.length, unit: '项' },
        running: {
          today: list.filter(i => i.status === 'running').length,
          annual: list.filter(i => i.status === 'running').length,
          unit: '项'
        },
        completed: {
          today: list.filter(i => i.status === 'completed').length,
          annual: list.filter(i => i.status === 'completed').length,
          unit: '项'
        },
        review: {
          today: list.filter(i => i.status === 'review').length,
          annual: list.filter(i => i.status === 'review').length,
          unit: '项'
        }
      }
    },
    buildStageChart(data) {
      this.stageChartOption = {
        tooltip: { trigger: 'item', formatter: '{b}: {d}%' },
        legend: { orient: 'vertical', right: 10, top: 'center' },
        color: ['#1a3a5c', '#409EFF', '#E6A23C', '#67C23A', '#909399'],
        series: [{
          type: 'pie',
          radius: ['40%', '65%'],
          center: ['40%', '50%'],
          data: data.map(d => ({ name: d.name, value: d.value })),
          label: { formatter: '{b}\n{d}%' }
        }]
      }
    },
    buildTrendChart(trend) {
      this.trendChartOption = {
        tooltip: { trigger: 'axis' },
        grid: { left: 50, right: 20, top: 30, bottom: 40 },
        xAxis: { type: 'category', data: trend.months },
        yAxis: { type: 'value', name: '项', minInterval: 1 },
        series: [{
          type: 'line',
          data: trend.completedCount,
          smooth: true,
          itemStyle: { color: '#409EFF' },
          areaStyle: { color: 'rgba(64, 158, 255, 0.15)' }
        }]
      }
    },
    statusTagType(status) {
      const map = {
        pending: 'info',
        running: 'warning',
        review: '',
        completed: 'success'
      }
      return map[status] || 'info'
    },
    openCreateDialog() {
      this.createDialogVisible = true
    },
    resetCreateForm() {
      this.createForm = {
        modelId: '',
        functionalUnit: '',
        year: '2025',
        boundary: '从摇篮到大门'
      }
      if (this.$refs.createForm) {
        this.$refs.createForm.resetFields()
      }
    },
    handleCreate() {
      this.$refs.createForm.validate(valid => {
        if (!valid) return
        const model = this.publishedModels.find(m => m.id === this.createForm.modelId)
        if (!model) return
        const taskNo = `CF-2025-${String(this.nextTaskNo++).padStart(3, '0')}`
        this.allTasks.unshift({
          id: `acc-${Date.now()}`,
          taskNo,
          productName: model.productName,
          modelId: model.id,
          modelName: model.name,
          functionalUnit: this.createForm.functionalUnit,
          footprint: 0,
          standard: model.standard,
          year: Number(this.createForm.year),
          status: 'running',
          statusLabel: '进行中',
          completedAt: '-',
          stageBreakdown: []
        })
        this.createDialogVisible = false
        this.applyFilters()
        this.$message.success('核算任务已发起')
      })
    },
    openDetail(row) {
      this.currentTask = row
      this.drawerVisible = true
    },
    handleSubmit(row) {
      row.status = 'review'
      row.statusLabel = '待审核'
      this.$message.success('已提交审核')
    },
    goEvaluation(row) {
      this.$router.push({ path: '/carbon-footprint/evaluation', query: { taskId: row.id } })
    }
  }
}
</script>

<style lang="less" scoped>
@import '~@/styles/variables.less';

.page-header {
  margin-bottom: 16px;

  .page-title {
    font-size: 18px;
    font-weight: 600;
    color: @text-primary;
    margin: 0;
  }
}

.stat-cards-row {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 16px;
  margin-bottom: 16px;
}

.chart-section {
  display: flex;
  gap: 16px;
  height: 320px;
  margin-bottom: 16px;

  .stage-chart,
  .trend-chart {
    flex: 1;
  }
}

.table-section {
  background: #fff;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
  padding: 16px;
}

.table-toolbar {
  margin-bottom: 12px;
}

.drawer-content {
  padding: 0 4px;
}

.detail-block {
  margin-bottom: 20px;

  .block-title {
    font-size: 14px;
    font-weight: 600;
    margin-bottom: 8px;
    padding-left: 8px;
    border-left: 3px solid @active-color;
  }
}
</style>
